import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { I18n, Translate } from "react-redux-i18n";
import { connect } from "react-redux";
import {
	SearchInput,
	Icons,
	DataTable,
	Button,
	Pagination,
} from "cakemail-ui-components/lib/react";
import { getContactsOfListService } from "assets/js/services/contactService";
import { getCalendarFormat } from "utils/dateTimeUtils";
import { getLanguageLabel } from "utils/languages";
import { withRouter } from "react-router-dom";
import promisePool from "es6-promise-pool";
import {
	contactListCountCachePromisePool,
	getListsCache,
	promisePoolCaching,
	setListsCache,
} from "utils/localStorageUtil";

export const ViewListOverlay = (props) => {
	const [search, setSearch] = useState(props.initialSearch);
	const [lists, setLists] = useState(props.initialLists);
	const [sort, setSort] = useState("-created_on");
	const [sortedItemIndex, setSortedItemIndex] = useState(2);
	const [perPage, setPerPage] = useState(25);
	const [page, setPage] = useState(1);
	const [contactsOfListsMemo, setContactsOfListsMemo] = useState(
		getListsCache()
	);

	useEffect(() => {
		contactListCountCachePromisePool(lists.data, setContactsOfListsMemo);
	}, [lists]);

	function getOptions(searchParam, sortParam, pageParam, perPageParam) {
		const searchValue = searchParam || search;
		const sortValue = sortParam || sort;
		const pageValue = pageParam || page;
		const perPageValue = perPageParam || perPage;

		const options = {
			per_page: perPageValue,
			page: pageValue,
			sort: sortValue,
		};

		if (searchParam) {
			options.filter = `name==*${searchValue}*`;
		}

		return options;
	}

	function sortHandler(e, index, sorting, colExtra) {
		setSortedItemIndex(index);
		const sortValue = (sorting === "asc" ? "+" : "-") + colExtra;
		setSort(sortValue);

		props.filterLists(getOptions(null, sortValue), false, (data) => {
			setLists(data);
		});
	}

	function selectList(e, list) {
		//simulate the dropdown onChange
		props.onListSelect(
			{
				...e,
				currentTarget: {
					dataset: {
						value: "" + list.id,
					},
				},
				target: {
					dataset: {
						value: "" + list.id,
					},
				},
			},
			{ name: props.name },
			{ value: "" + list.id, label: list.name },
			null
		);
	}

	function paginationHandler(e, perPage, page) {
		setPerPage(perPage);
		setPage(page);

		props.filterLists(getOptions(null, null, page, perPage), false, (data) => {
			setLists(data);
		});
	}

	function onSearchChange(curSearch) {
		setSearch(curSearch);
		props.onSearchChange(curSearch);

		props.filterLists(getOptions(curSearch), false, (data) => {
			setLists(data);
		});
	}

	const headerContent = [
		{
			content: (
				<p>
					<Translate
						value="app-general-lists-search-found"
						count={
							!isNaN(lists?.pagination?.count) ? lists?.pagination?.count : "#"
						}
					/>
				</p>
			),
			isSorting: sortedItemIndex === 0,
			extra: "name",
		},
		{
			content: <Translate value="app-contact-all-PH-contacs" />,
			notSortable: true,
		},
		{
			content: (
				<p>
					<Translate value="app-general-date-created" />
				</p>
			),
			isSorting: sortedItemIndex === 2,
			extra: "created_on",
		},
		{
			content: <Translate value="app-list-manage-default-language-title" />,
			notSortable: true,
		},
		{ content: <p style={{ visibility: "hidden" }}>#</p>, notSortable: true },
	];

	let tableContent = [];

	if (lists) {
		tableContent = lists.data.map((list) => {
			const listId = "" + list.id;
			return {
				cols: [
					{ content: list.name },
					{
						content:
							contactsOfListsMemo?.[listId]?.contactsCount != null
								? contactsOfListsMemo[listId].contactsCount
								: "-",
					},
					{ content: getCalendarFormat(list.created_on, false) },
					{ content: getLanguageLabel("ptbr") },
					{
						content: (
							<>
								{"" + props?.selectedListId === "" + list.id ? (
									<Icons name="micro-glyph-checkmark" />
								) : (
									<Button
										size="xsmall"
										text={<Translate value="app-general-B-select" />}
										shapeStyle="stroke"
										colorTheme="grey"
										onClick={(e) => {
											selectList(e, list);
										}}
									/>
								)}
							</>
						),
					},
				],
			};
		});
	}

	return (
		<div className="listSearch-overlay-content">
			<SearchInput
				search={search}
				expendable={false}
				placeholder={I18n.t("app-general-list-search-all", {
					count: props.total,
				})}
				onChange={onSearchChange}
				clearable
				clearText={
					<>
						<Icons name="glyphs-close" />{" "}
						<Translate value="app-general-search_clear-txt" />
					</>
				}
				fieldStyle="boxed"
			/>

			<DataTable
				headerContent={headerContent}
				tableContent={tableContent}
				lastStickyColumnCount={1}
				sort={sort[0] === "-" ? "desc" : "asc"}
				enableSort
				selectedColIndex={sortedItemIndex}
				onHeadingClick={sortHandler}
				typeStyle="minimal"
			/>
			{lists?.data?.length > 25 && (
				<Pagination
					perPage={perPage}
					page={page}
					currentCount={props.total}
					onChange={paginationHandler}
					perPageText={<Translate value="app-contact-all-PA-per_page" />}
					ofText={<Translate value="app-contact-all-PA-of" />}
				/>
			)}
		</div>
	);
};
ViewListOverlay.propTypes = {
	initialSearch: PropTypes.string,
	initialLists: PropTypes.object,
	filterLists: PropTypes.func,
	total: PropTypes.number,
	onSearchChange: PropTypes.func,
	onListSelect: PropTypes.func,
	selectedListId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	accountId: PropTypes.string,
	name: PropTypes.string,
};

export default withRouter(
	connect((state) => {
		return {
			accountId: state.global.account.id,
		};
	}, null)(ViewListOverlay)
);
